import {currency} from '../../util';
import {PricingUi} from './pricing-request';

/** Keeps the price's line box while the first pricing response is pending, without showing a number. */
export const PRICE_PLACEHOLDER = ' ';

export type PriceDisplay = {
    /** False until /api/pricing has answered once. */
    ready: boolean;
    /** True when the resolved price carries a store discount (list price shown struck through). */
    hasDiscount: boolean;
    /** Formatted price to show: the discounted price when hasDiscount, otherwise the list price; a blank placeholder until ready. */
    current: string;
    /** Formatted list price for the struck-through "was" price; a blank placeholder until ready. */
    original: string;
};

// Until the first /api/pricing response, ui.price holds the cached store list price and ui.price_discount
// its default of 0. Comparing those two made every product page open with a "discounted" red $0.00 next to
// a struck-through list price (FreshService 10265), and the cached list price itself is often not the
// live price - so neither value is shown before the response arrives.
export const getPriceDisplay = (ui: Pick<PricingUi, 'price' | 'price_discount' | 'price_resolved'>): PriceDisplay => {
    if (!ui.price_resolved) {
        return {ready: false, hasDiscount: false, current: PRICE_PLACEHOLDER, original: PRICE_PLACEHOLDER};
    }
    const hasDiscount = ui.price !== ui.price_discount;
    return {
        ready: true,
        hasDiscount,
        current: currency(hasDiscount ? ui.price_discount : ui.price),
        original: currency(ui.price),
    };
};

/** The first price already on screen is not announced. A later change is. The first number after the placeholder is announced. */
export const priceChangeMessage = (previous: string | null, display: PriceDisplay | null): {previous: string | null; message: string | null} => {
    if (!display || !display.ready || display.current === PRICE_PLACEHOLDER || display.current.trim() === '') {
        return {previous: previous === null ? '' : previous, message: null};
    }

    const message = display.hasDiscount ? `Sale price ${display.current}, regular price ${display.original}` : `Price ${display.current}`;
    if (previous === null || previous === message) {
        return {previous: message, message: null};
    }

    return {previous: message, message};
};
